import { Copy, ExternalLink, Globe, LayoutDashboard, MonitorSmartphone, Plus, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { displayUrl, toHref } from '../links'
import type { Customer } from '../types'

const LINKS: { key: 'adminUrl' | 'appUrl' | 'website'; label: string; icon: LucideIcon; tone: string }[] = [
  { key: 'adminUrl', label: 'crm.links.admin', icon: LayoutDashboard, tone: 'bg-violet/12 text-violet' },
  { key: 'appUrl', label: 'crm.links.app', icon: MonitorSmartphone, tone: 'bg-primary/12 text-primary' },
  { key: 'website', label: 'crm.links.website', icon: Globe, tone: 'bg-info/12 text-info' },
]

type Props = { customer: Customer; canWrite: boolean; onEdit?: () => void; variant?: 'tiles' | 'list' }

/** Projenin canlı ortam bağlantıları: yönetim paneli, uygulama (site) ve müşterinin web sitesi. */
export function LiveLinks({ customer, canWrite, onEdit, variant = 'list' }: Props) {
  const { t } = useTranslation()
  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(toHref(url))
      toast.success(t('crm.links.copied'))
    } catch {
      toast.error(t('crm.links.copyFailed'))
    }
  }

  return (
    <ul className={cn(variant === 'tiles' ? 'grid grid-cols-1 gap-3 md:grid-cols-3' : 'space-y-2')}>
      {LINKS.map(({ key, label, icon: Icon, tone }) => {
        const url = customer[key]
        return (
          <li key={key} className={cn('flex min-w-0 items-center gap-3 rounded-lg border p-3', variant === 'tiles' && 'bg-card shadow-card', !url && 'border-dashed')}>
            <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg', url ? tone : 'bg-muted text-muted-foreground')}>
              <Icon className="size-4" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">{t(label)}</p>
              {url ? (
                <a href={toHref(url)} target="_blank" rel="noreferrer noopener" className="block truncate text-sm font-medium text-primary hover:underline" title={toHref(url)}>
                  {displayUrl(url)}
                </a>
              ) : (
                <p className="text-sm text-muted-foreground">{t('crm.links.none')}</p>
              )}
            </div>
            {url ? (
              <div className="flex shrink-0">
                <Button size="icon" className="size-8" variant="ghost" aria-label={t('crm.links.copy', { name: t(label) })} onClick={() => void copy(url)}>
                  <Copy />
                </Button>
                <Button size="icon" className="size-8" variant="ghost" asChild>
                  <a href={toHref(url)} target="_blank" rel="noreferrer noopener" aria-label={t('crm.links.open', { name: t(label) })}>
                    <ExternalLink />
                  </a>
                </Button>
              </div>
            ) : canWrite && onEdit ? (
              <Button size="icon" className="size-8" variant="ghost" aria-label={t('crm.links.add', { name: t(label) })} onClick={onEdit}>
                <Plus />
              </Button>
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}
